import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Check, Loader2, ArrowRight } from "lucide-react";
import { api, login } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const STEPS = [
  "Connexion à la boutique...",
  "Analyse de la page d'accueil...",
  "Analyse de l'expérience utilisateur...",
  "Analyse des fiches produit...",
  "Analyse mobile & responsive...",
  "Analyse des signaux de confiance...",
  "Analyse des performances...",
  "Génération des recommandations IA...",
];

export default function Analyze() {
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialUrl = params.get("url") || "";

  const [url, setUrl] = useState(initialUrl);
  const [storeType, setStoreType] = useState("");
  const [objective, setObjective] = useState("");
  const [running, setRunning] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const auditPromiseRef = useRef(null);

  const start = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return toast.error("Veuillez entrer une URL");
    if (!user) {
      toast.info("Connectez-vous pour lancer un audit");
      login();
      return;
    }
    // Client-side pre-check (server remains source of truth)
    if (user.plan === "free" && user.can_run_audit === false) {
      setQuotaBlocked(true);
      return;
    }
    setQuotaBlocked(false);
    setRunning(true);
    setStepIdx(0);
    auditPromiseRef.current = api.post("/audits/analyze", {
      url: url.trim(),
      store_type: storeType || null,
      objective: objective || null,
    });
  };

  useEffect(() => {
    if (!running) return;
    if (stepIdx >= STEPS.length - 1) return;
    const t = setTimeout(() => setStepIdx((i) => i + 1), 900);
    return () => clearTimeout(t);
  }, [running, stepIdx]);

  useEffect(() => {
    if (!running || !auditPromiseRef.current) return;
    // Wait for both min-step duration and API response.
    const minTime = new Promise((r) => setTimeout(r, STEPS.length * 900));
    Promise.all([auditPromiseRef.current, minTime])
      .then(([res]) => {
        navigate(`/results/${res.data.audit_id}`);
      })
      .catch((err) => {
        setRunning(false);
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 402) {
          setQuotaBlocked(true);
          refresh();
          return;
        }
        if (err?.response?.status === 422 && typeof detail === "object") {
          // Real-scan blocking error (SSRF blocked, DNS, 404, timeout, etc.)
          toast.error(detail.message || "Impossible d'auditer cette URL.");
          return;
        }
        if (err?.response?.status === 502 && typeof detail === "object") {
          toast.error(detail.message || "Erreur IA. Réessayez.");
          return;
        }
        const msg = typeof detail === "string" ? detail : detail?.message || "L'audit a échoué. Réessayez.";
        toast.error(msg);
      });
  }, [running, navigate, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080A11] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080A11] text-slate-100">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-xs font-semibold uppercase tracking-widest text-violet-300">Nouvel audit</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white">Analysons votre boutique</h1>
          <p className="mt-4 text-slate-400">Renseignez votre URL et laissez notre IA détecter ce qui freine vos ventes.</p>
        </div>

        {quotaBlocked ? (
          <div className="glass rounded-2xl p-10 text-center" data-testid="quota-blocked-card">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3" data-testid="quota-blocked-title">
              Votre audit gratuit a été utilisé
            </h2>
            <p className="text-slate-400 max-w-md mx-auto mb-8">
              Passez à Pro pour continuer à analyser vos boutiques. Audits illimités, rapport complet, historique et export PDF.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="quota-upgrade-pro-btn"
                onClick={() => navigate("/pricing")}
                className="h-12 px-8 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold border-0 shadow-lg shadow-violet-600/25"
              >
                Passer à Pro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="quota-back-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                variant="ghost"
                className="h-12 px-6 text-slate-300 hover:text-white hover:bg-slate-800/60"
              >
                Retour au dashboard
              </Button>
            </div>
          </div>
        ) : !running ? (
          <form onSubmit={start} className="glass rounded-2xl p-8 space-y-6">
            <div>
              <Label htmlFor="url" className="text-slate-300 text-sm mb-2 block">URL de la boutique *</Label>
              <Input
                id="url"
                data-testid="analyze-url-input"
                placeholder="votreboutique.myshopify.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-12 bg-[#080A11] border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="type" className="text-slate-300 text-sm mb-2 block">Type de boutique (facultatif)</Label>
              <Input
                id="type"
                data-testid="analyze-type-input"
                placeholder="mode, cosmétiques, tech..."
                value={storeType}
                onChange={(e) => setStoreType(e.target.value)}
                className="h-12 bg-[#080A11] border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
              />
            </div>
            <div>
              <Label htmlFor="obj" className="text-slate-300 text-sm mb-2 block">Objectif principal (facultatif)</Label>
              <Input
                id="obj"
                data-testid="analyze-objective-input"
                placeholder="augmenter les ventes, améliorer la conversion mobile..."
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="h-12 bg-[#080A11] border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
              />
            </div>
            <Button
              type="submit"
              data-testid="analyze-start-button"
              className="w-full h-13 py-3 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold text-base"
            >
              Lancer l'analyse <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Scan HTML réel de votre URL + audit IA Claude Sonnet 5 grounded sur les données extraites.
            </p>
          </form>
        ) : (
          <div className="glass rounded-2xl p-8" data-testid="analysis-progress-container">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center animate-pulse">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-white">Analyse en cours</div>
                <div className="text-xs text-slate-500 font-mono-alt">{url}</div>
              </div>
            </div>

            <div className="space-y-3">
              {STEPS.map((step, i) => {
                const state = i < stepIdx ? "done" : i === stepIdx ? "active" : "pending";
                return (
                  <div
                    key={step}
                    data-testid={`analysis-step-${i}`}
                    className={`flex items-center gap-3 text-sm py-2 transition-all ${
                      state === "done" ? "text-emerald-400" :
                      state === "active" ? "text-white" : "text-slate-600"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      state === "done" ? "bg-emerald-500/20" :
                      state === "active" ? "bg-violet-500/20" : "bg-slate-800"
                    }`}>
                      {state === "done" ? <Check className="w-3 h-3" /> :
                        state === "active" ? <Loader2 className="w-3 h-3 animate-spin" /> :
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />}
                    </div>
                    <span>{step}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                data-testid="analysis-progress-bar"
                className="h-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all duration-500"
                style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
